import { useLocation } from 'react-router-dom';
import { useCurrentAgreement } from '@economic/react-app';
import useAppSettings from '../../shared/hooks/useAppSettings';

export enum InboxHanger {
    Inbox = 'inbox',
    AddDocuments = 'addDocuments',
    SmartInboxTrial = 'smartScanTrial',
    SmartScanSuggestion = 'smartScanSuggestion',
}

export function useInboxHanger(hanger = InboxHanger.Inbox): [boolean, () => void] {
    const agreement = useCurrentAgreement();
    const location = useLocation();
    const showHangerSettings = useAppSettings('inboxhangar');
    const notOnHomePage = location.pathname !== '/home';
    const hasInboxHangerFeatureFlag = agreement.features.inboxHanger;
    const hasSmartInboxTrialFeatureFlag = agreement.features.smartInboxTrialHanger;

    function hangerDismissedSetting(hanger: InboxHanger) {
        switch (hanger) {
            case InboxHanger.Inbox:
                return 'isHangerDismissed';
            case InboxHanger.AddDocuments:
                return 'isAddDocumentsHangerDismissed';
            case InboxHanger.SmartInboxTrial:
                return 'isSmartInboxTrialDismissed';
            case InboxHanger.SmartScanSuggestion:
                return 'isSmartScanSuggestionDismissed';
        }
    }

    function hideHanger(hanger: InboxHanger): () => void {
        return () => {
            showHangerSettings.set({ ...showHangerSettings.data?.content, [hangerDismissedSetting(hanger)]: true });
        };
    }

    const isHangerDismissed = (hanger: InboxHanger) => {
        return showHangerSettings.data?.content?.[hangerDismissedSetting(hanger)] ?? false;
    };

    const hideWhenVisitingInbox = (hanger: InboxHanger) => {
        if (
            (hanger === InboxHanger.Inbox || hanger === InboxHanger.SmartInboxTrial) &&
            showHanger(hanger) &&
            (location.pathname === '/inbox' || location.pathname.startsWith('/inbox/'))
        ) {
            // Hide inbox hanger when visiting /inbox
            hideHanger(hanger)();
        }
    };

    const showHanger = (hanger: InboxHanger) => {
        const canDisplayHanger = (hanger: InboxHanger) => {
            switch (hanger) {
                case InboxHanger.Inbox:
                    return hasInboxHangerFeatureFlag && notOnHomePage && !hasSmartInboxTrialFeatureFlag;
                case InboxHanger.AddDocuments:
                    return hasInboxHangerFeatureFlag && !hasSmartInboxTrialFeatureFlag;
                case InboxHanger.SmartInboxTrial:
                    return hasSmartInboxTrialFeatureFlag;
                case InboxHanger.SmartScanSuggestion:
                    return hasSmartInboxTrialFeatureFlag;
            }
        };

        return !showHangerSettings.loading && !showHangerSettings.error && !isHangerDismissed(hanger) && canDisplayHanger(hanger);
    };

    hideWhenVisitingInbox(hanger);

    return [showHanger(hanger), hideHanger(hanger)];
}
